/* eslint-disable consistent-return */
import {
  type MasonryEngineActionHandlerMap,
  type MasonryEngineActionHandler,
} from '@leagueplatform/masonry-engine';
import type {
  MasonryDriverAction,
  MasonryDeeplinkAction,
  MasonryDismissAction,
  MasonryUndismissAction,
  MasonryAnalyticsAction,
  MasonrySendAnalyticsAction,
  MasonryOnLoadAction,
  MasonryReloadAction,
  MasonryReloadOnResizeAction,
  MasonryHideAction,
  MasonryShowAction,
  MasonryToggleVisibilityAction,
  MasonrySetNodeStateAction,
  MasonryShowModalWithPropertiesAction,
  MasonryShowModalAction,
  MasonryDismissModalAction,
  MasonryDownloadAction,
  MasonryHandleActionExceptionAction,
  MasonryNoOpReturnStatusAction,
  MasonryInvokeDataProcessorAction,
} from '@leagueplatform/masonry-renderers';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import {
  postMasonryNodeDismiss,
  deleteMasonryNodeDismiss,
  createHiddenNode,
  createAsyncNode,
  warnMissingAnalyticsFields,
  postDataProcessor,
} from '@leagueplatform/masonry-api';
import { downloadBlobAsFile } from '@leagueplatform/web-common';
import { leagueFetch } from '@leagueplatform/league-fetch';
import { captureError } from '@leagueplatform/observability';

import { logError } from '#utils/log-error.ts';
import { handleMasonryLink } from '#utils/handle-masonry-link.util.ts';
import { MasonryToggleFormFieldVisibilityActionHandler } from './action-handlers/toggle-form-field-visibility';
import { SubmitFormActionHandler } from './action-handlers/submit-form';
import { SetFormStatusBannerActionHandler } from './action-handlers/set-form-status-banner';
import { submitFormAndReloadActionHandler } from './action-handlers/submit-form-and-reload';
import { SubmitFlowStepActionHandler } from './action-handlers/submit-flow-step';
import { ValidateFlowStepActionHandler } from './action-handlers/validate-flow-step';
import { extraQueryParamsToRecord } from './utils/extra-query-params-to-record';
import { masonrySetTitleBaseActionHandler as SetTitleActionHandler } from './misc/set-title-base-action-handler';

const trackMasonryAnalytics = (event: EVENT_NAME, widgetProperties: object) =>
  trackAnalyticsEvent(event, {
    ...widgetProperties,
  });

export const DeeplinkActionHandler: MasonryEngineActionHandler<
  MasonryDeeplinkAction,
  MasonryDriverAction
> = (action, { navigate }) => {
  const { payload, event } = action;

  if (!payload.url) {
    event?.preventDefault();
    return {
      isSuccess: false,
      error: new Error('DeeplinkActionHandler: Missing required URL'),
    };
  }

  handleMasonryLink({
    navigate,
    url: payload.url,
    event,
    state: payload.state,
  });
};

const DismissActionHandler: MasonryEngineActionHandler<
  MasonryDismissAction,
  MasonryDriverAction
> = async (action, { invokeAction, messagingController }) => {
  const { ancestry, analytics, payload } = action;
  const nodeId = ancestry[0];
  const { nodeIds, dismissedUntil, forceProcess } = payload;

  const impressionId = analytics?.payload.impression_id || nodeId;

  await Promise.all([
    // Send a POST request to the API to dismiss the node
    postMasonryNodeDismiss({
      nodeIds,
      dismissedUntil,
      forceProcess,
      id: impressionId,
    }),
    // Emit an action to hide nodes specified in the payload
    invokeAction({
      type: 'hide' as const,
      namespace: 'default',
      ancestry,
      payload: { nodeIds },
    }),
  ]);

  /**
   * In addition to setting `isHidden` to `true` at the state controller level,
   * we also proactively seed the query cache for this node ID to BE a "hidden node", which is
   * exactly what data the backend sends for nodes that have been dismissed. Doing this
   * proactively means that if the user encounters this node again in cached data form - for example,
   * leaving this page and then returning to it - the node's original cached data (in which it was not hidden)
   * will not flash briefly before being hidden again.
   */
  nodeIds.forEach((_id) => {
    messagingController.sendMessageToInstance({
      type: 'replaceAsyncNodeCache',
      payload: {
        nodeData: createHiddenNode(_id),
      },
    });
  });
};

const UndismissActionHandler: MasonryEngineActionHandler<
  MasonryUndismissAction,
  MasonryDriverAction
> = async (action, { invokeAction }) => {
  const { ancestry, analytics, payload } = action;
  const nodeId = ancestry[0];
  const { nodeIds } = payload;
  const id = analytics?.payload.impression_id || nodeId;

  if (nodeId) {
    await Promise.all([
      // Send a DELETE request to the API to undismiss the node
      deleteMasonryNodeDismiss({ nodeIds, id }),
      // Emit an action to show nodes specified in the payload
      invokeAction({
        type: 'show' as const,
        namespace: 'default',
        ancestry,
        payload: {
          nodeIds,
        },
      }),
    ]);
  }
};

// Hide Nodes action handler. Payload requires an array of node ids
const HideActionHandler: MasonryEngineActionHandler<
  MasonryHideAction,
  MasonryDriverAction
> = (action, { stateController }) => {
  action.payload.nodeIds?.forEach((nodeId: string) => {
    stateController.setNodeState(nodeId, (state) => ({
      ...state,
      isHidden: true,
    }));
  });
};

// Show Nodes action handler. Payload requires an array of node ids
const ShowActionHandler: MasonryEngineActionHandler<
  MasonryShowAction,
  MasonryDriverAction
> = (action, { stateController }) => {
  action.payload.nodeIds?.forEach((nodeId: string) => {
    stateController.setNodeState(nodeId, (state) => ({
      ...state,
      isHidden: false,
    }));
  });
};

// Show/Hide Nodes action handler based on the previous state. Payload requires an array of node ids
const ToggleVisibilityActionHandler: MasonryEngineActionHandler<
  MasonryToggleVisibilityAction,
  MasonryDriverAction
> = (action, { stateController }) => {
  action.payload.nodeIds?.forEach((nodeId: string) => {
    stateController.setNodeState(nodeId, (state) => ({
      ...state,
      isHidden: !state.isHidden,
    }));
  });
};

const AnalyticsActionHandler: MasonryEngineActionHandler<
  MasonryAnalyticsAction,
  MasonryDriverAction
> = async (action) => {
  const { event_name: eventName, payload } = action.payload;
  if (eventName && payload) {
    warnMissingAnalyticsFields(
      `Backend-driven analytics action "${eventName}"`,
      {
        product_area: payload.product_area,
        sub_product_area: payload.sub_product_area,
        screen_name: payload.screen_name,
      },
    );
    trackMasonryAnalytics(eventName, payload);
  }
};

const SendAnalyticsActionHandler: MasonryEngineActionHandler<
  MasonrySendAnalyticsAction,
  MasonryDriverAction
> = () => {};

// An action handler for onLoad actions
const OnLoadActionHandler: MasonryEngineActionHandler<
  MasonryOnLoadAction,
  MasonryDriverAction
> = () => {};

/**
 * Reload action handler that reloads specified nodes
 */
const ReloadActionHandler: MasonryEngineActionHandler<
  MasonryReloadAction,
  MasonryDriverAction
> = async (action, { messagingController }) => {
  const result = await Promise.all(
    action.payload.nodeIds.map((nodeId) =>
      messagingController.sendMessage(nodeId, {
        type: 'reload',
        payload: {
          renderIntermediately: action.payload.renderIntermediately || 'loader',
          setQueryParams: (current) => {
            const paramsClone = {
              ...current,
            };

            const extraParamsObject = Array.isArray(
              action.payload.extraQueryParams,
            )
              ? extraQueryParamsToRecord(action.payload.extraQueryParams)
              : {};

            Object.entries(extraParamsObject).forEach(([key, value]) => {
              if (value) {
                paramsClone[key] = value;
              } else {
                delete paramsClone[key];
              }
            });

            return paramsClone;
          },
        },
      }),
    ),
  );

  return {
    isSuccess: true,
    data: result,
  };
};

/**
 *  This action is used to reload nodes when the viewport is resized.
 */
const ReloadOnResizeActionHandler: MasonryEngineActionHandler<
  MasonryReloadOnResizeAction,
  MasonryDriverAction
> = async (action, { invokeAction }) => {
  await invokeAction({
    type: 'reload',
    namespace: action.namespace,
    ancestry: action.ancestry,
    payload: {
      nodeIds: [action.ancestry[0]],
    },
  });
};

const MasonrySetNodeStateActionHandler: MasonryEngineActionHandler<
  MasonrySetNodeStateAction,
  MasonryDriverAction
> = (action, { stateController }) => {
  const nodeId = action.payload.nodeId || action.ancestry[0];

  // set form field value
  stateController.setNodeState(nodeId, (state) => ({
    ...state,
    ...action.payload.state,
  }));
};

export const MasonryShowModalWithPropertiesActionHandler: MasonryEngineActionHandler<
  MasonryShowModalWithPropertiesAction,
  MasonryDriverAction
> = async (action, { messagingController }) => {
  let elementToFocusOnClose = (action.currentTarget ||
    document.activeElement) as HTMLElement | null;
  if (!elementToFocusOnClose) {
    console.error(
      'The showModalWithProperties action must include an original DOM event when invoked, so that the opening DOM element can be focused once the modal is dismissed!',
    );

    elementToFocusOnClose = document.body;
  }

  const { heading, headingLevel, headingSize, alignment, ...rest } =
    action.payload;

  messagingController.sendMessageToInstance({
    type: 'showModal',
    payload: {
      title: heading,
      hideTitle: false,
      headingLevel,
      headingSize,
      showCloseButton: true,
      content: { kind: 'text', ...rest },
      alignment,
      elementToFocusOnClose,
    },
  });
};

export const MasonryShowModalActionHandler: MasonryEngineActionHandler<
  MasonryShowModalAction,
  MasonryDriverAction
> = async (action, { stateController, messagingController }) => {
  let elementToFocusOnClose = (action.currentTarget ||
    document.activeElement) as HTMLElement | null;
  if (!elementToFocusOnClose) {
    console.error(
      'The showModal action must include an original DOM event when invoked, so that the opening DOM element can be focused once the modal is dismissed!',
    );

    elementToFocusOnClose = document.body;
  }

  const { nodeId, prefetch, forceFreshLoad, extraQueryParams, ...rest } =
    action.payload;

  const initialQueryParams = extraQueryParams
    ? extraQueryParamsToRecord(extraQueryParams)
    : {};

  const node = createAsyncNode({
    id: action.payload.nodeId,
    initialQueryParams,
  });

  if (forceFreshLoad) {
    stateController.instance.asyncNodeCacheSeeds.delete(action.payload.nodeId);
  }

  messagingController.sendMessageToInstance({
    type: 'showModal',
    payload: {
      content: { kind: 'node', node },
      ...rest,
      elementToFocusOnClose,
    },
  });
};

export const MasonryDismissModalActionHandler: MasonryEngineActionHandler<
  MasonryDismissModalAction,
  MasonryDriverAction
> = async (action, { messagingController }) => {
  messagingController.sendMessageToInstance({
    type: 'dismissModal',
    payload: {},
  });
};

export const MasonryDownloadActionHandler: MasonryEngineActionHandler<
  MasonryDownloadAction,
  MasonryDriverAction
> = async (action, { invokeAction, stateController }) => {
  const { payload, ancestry } = action;
  const { fileName, url, nodeIds: targetNodeIds, mimeType } = payload;

  if (!fileName || !url || !mimeType) {
    const errorMessage =
      'MasonryDownloadActionHandler: Missing required parameters';
    logError(errorMessage);
    return { isSuccess: false, error: new Error(errorMessage) };
  }

  const nodeIds = targetNodeIds?.length ? targetNodeIds : [ancestry[0]];

  // Start download
  nodeIds.forEach((nodeId) => {
    stateController.setNodeState(nodeId, (state) => ({
      ...state,
      loading: true,
    }));
  });

  try {
    const response = await leagueFetch(url, {
      headers: { Accept: mimeType },
    });

    if (!response.ok) {
      throw new Error(`MasonryDownloadActionHandler error: ${response.status}`);
    }

    const blob = await response.blob();
    downloadBlobAsFile(blob, fileName);

    // Ending download - success case
    nodeIds.forEach((nodeId) => {
      stateController.setNodeState(nodeId, (state) => ({
        ...state,
        loading: false,
      }));
    });
  } catch (error) {
    const normalizedError =
      error instanceof Error ? error : new Error(String(error));
    captureError(normalizedError);
    const errorMessage = `MasonryDownloadActionHandler: Failed to download file - ${normalizedError.message}`;
    logError(errorMessage);

    // Ensure loading states are reset on error
    nodeIds.forEach((nodeId) => {
      stateController.setNodeState(nodeId, (state) => ({
        ...state,
        loading: false,
      }));
    });

    // Show error modal
    await invokeAction({
      type: 'showModalWithProperties',
      namespace: action.namespace,
      ancestry: action.ancestry,
      event: action.event,
      currentTarget: action.currentTarget,
      payload: {
        heading: 'Download failed',
        body: 'Please try again later.',
        ctaLabel: 'Dismiss',
      },
    });

    return { isSuccess: false, error: normalizedError };
  }
};

const HandleActionExceptionActionHandler: MasonryEngineActionHandler<
  MasonryHandleActionExceptionAction,
  MasonryDriverAction
> = () => ({
  isSuccess: true,
});

/**
 * DO NOT USE THIS ACTION HANDLER IN PRODUCTION.
 * This action handler is used to test action chaining only.
 * This action handler is used to return a success or failure status based on the status payload.
 * If the status is 'exception', an error will be thrown.
 * If the status is 'failure', a failure status will be returned.
 * If the status is 'success', a success status will be returned.
 */
const NoOpReturnStatusActionHandler: MasonryEngineActionHandler<
  MasonryNoOpReturnStatusAction,
  MasonryDriverAction
> = async (action) => {
  const { status } = action.payload;

  switch (status) {
    case 'exception':
      throw new Error('Exception status triggered');
    case 'failure':
      return { isSuccess: false, error: 'Failure status triggered' };
    case 'success':
    default:
      return { isSuccess: true };
  }
};

const InvokeDataProcessorActionHandler: MasonryEngineActionHandler<
  MasonryInvokeDataProcessorAction,
  MasonryDriverAction
> = async (action, { invokeAction }) => {
  const { dataProcessorId, fields, suppressErrorModal } = action.payload;

  try {
    const response = await postDataProcessor({ dataProcessorId, fields });

    if (response.status === 'error' || response.status === 'warning') {
      if (suppressErrorModal !== true) {
        // Show error modal
        await invokeAction({
          type: 'showModalWithProperties',
          namespace: 'default',
          ancestry: action.ancestry,
          event: action.event,
          currentTarget: action.currentTarget,
          payload: {
            heading: 'Something went wrong',
            body: response.message || 'Please try again later.',
            ctaLabel: 'Dismiss',
          },
        });
      }

      return {
        isSuccess: false,
        error: response.errors || response.message,
        data: response,
      };
    }

    return { isSuccess: true, data: response };
  } catch (error) {
    const errorMessage = `InvokeDataProcessorActionHandler: Failed to invoke data processor - ${error}`;
    logError(errorMessage);

    if (suppressErrorModal !== true) {
      await invokeAction({
        type: 'showModalWithProperties',
        namespace: 'default',
        ancestry: action.ancestry,
        event: action.event,
        currentTarget: action.currentTarget,
        payload: {
          heading: 'Something went wrong',
          body: (error as Error).message || 'Please try again later.',
          ctaLabel: 'Dismiss',
        },
      });
    }

    throw error;
  }
};

export const masonryDriverActionHandlerMap: MasonryEngineActionHandlerMap<MasonryDriverAction> =
  {
    deeplink: DeeplinkActionHandler,
    dismiss: DismissActionHandler,
    download: MasonryDownloadActionHandler,
    undismiss: UndismissActionHandler,
    analytics: AnalyticsActionHandler,
    sendAnalytics: SendAnalyticsActionHandler,
    onLoad: OnLoadActionHandler,
    hide: HideActionHandler,
    show: ShowActionHandler,
    reload: ReloadActionHandler,
    reloadOnResize: ReloadOnResizeActionHandler,
    toggleVisibility: ToggleVisibilityActionHandler,
    submitForm: SubmitFormActionHandler,
    setNodeState: MasonrySetNodeStateActionHandler,
    showModalWithProperties: MasonryShowModalWithPropertiesActionHandler,
    showModal: MasonryShowModalActionHandler,
    dismissModal: MasonryDismissModalActionHandler,
    toggleFormFieldVisibility: MasonryToggleFormFieldVisibilityActionHandler,
    setFormStatusBanner: SetFormStatusBannerActionHandler,
    submitFormAndReload: submitFormAndReloadActionHandler,
    setTitle: SetTitleActionHandler,
    submitFlowStep: SubmitFlowStepActionHandler,
    validateFlowStep: ValidateFlowStepActionHandler,
    handleActionException: HandleActionExceptionActionHandler,
    noOpReturnStatus: NoOpReturnStatusActionHandler,
    invokeDataProcessor: InvokeDataProcessorActionHandler,
  };
